import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Checkbox,
    Stack,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    SyncAlt as TransferIcon,
    ArrowBack as BackIcon,
    ArrowForward as NextIcon,
    CheckCircle as CheckIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFactures } from '../../data/slices/factureSlice';
import PageHeader from '../../components/layout/PageHeader';
import { formatCurrency } from '../../utils/compteClientEngine';
import {
    getEligibleFactures,
    generateSageEcritures,
    buildSageExportFileContent,
    downloadSageFile,
    saveTransferRecord,
    generateNextSelectionNumber,
    generateNextSimulationNumber,
    generateNextTransferNumber,
    getSageSettings
} from '../../utils/sageAccountingEngine';
import ListeSelectionViewer from './components/ListeSelectionViewer';
import PreBasculeSimulationViewer from './components/PreBasculeSimulationViewer';
import BordereauValidationViewer from './components/BordereauValidationViewer';
import dayjs from 'dayjs';

const STEPS = [
    'Sélection des écritures',
    'Contrôle de la sélection',
    'Simulation (Pré-transfert)',
    'Validation du transfert'
];

const TransfertEcrituresPage = () => {
    const dispatch = useDispatch();
    const { factures, fetchStatus } = useSelector((state) => state.factures);
    const authState = useSelector((state) => state.auth || state.user);
    const currentUser = authState?.user?.name || authState?.user?.nom || 'Comptable AFT';

    const [activeStep, setActiveStep] = useState(0);

    // Paramètres de filtrage Étape 1
    const [dateDebut, setDateDebut] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
    const [dateFin, setDateFin] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));

    // Factures sélectionnées
    const [selectedFactureIds, setSelectedFactureIds] = useState(new Set());

    // Numéros de suivi officiels
    const [selectionNumber, setSelectionNumber] = useState('');
    const [simulationNumber, setSimulationNumber] = useState('');
    const [transferRecord, setTransferRecord] = useState(null);

    useEffect(() => {
        dispatch(fetchFactures());
    }, [dispatch]);

    // Extraction des factures éligibles (non transférées, date comprise)
    const eligibleFactures = useMemo(() => {
        return getEligibleFactures(factures, dateDebut, dateFin);
    }, [factures, dateDebut, dateFin]);

    // Initialisation / mise à jour de la sélection par défaut
    useEffect(() => {
        if (eligibleFactures.length > 0 && selectedFactureIds.size === 0 && activeStep === 0) {
            // Par défaut, cocher toutes les factures éligibles trouvées
            const allIds = new Set(eligibleFactures.map(f => f.id));
            setSelectedFactureIds(allIds);
        }
    }, [eligibleFactures, activeStep, selectedFactureIds.size]);

    // Factures effectivement cochées
    const checkedFactures = useMemo(() => {
        return eligibleFactures.filter(f => selectedFactureIds.has(f.id));
    }, [eligibleFactures, selectedFactureIds]);

    // Totaux dynamiques Étape 1
    const totalSelectedHT = checkedFactures.reduce((s, f) => s + Number(f.montantHT || 0), 0);
    const totalSelectedTVA = checkedFactures.reduce((s, f) => s + Number(f.tva || 0), 0);
    const totalSelectedTTC = checkedFactures.reduce((s, f) => s + Number(f.montantTTC || 0), 0);

    // Simulation comptable pour Étape 2 & 3
    const simulationResult = useMemo(() => {
        if (checkedFactures.length === 0) return { ecritures: [], comptesRecap: [], anomalies: [], controleStats: [], isEquilibre: true };
        return generateSageEcritures(checkedFactures);
    }, [checkedFactures]);

    const handleToggleFacture = (id) => {
        setSelectedFactureIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedFactureIds.size === eligibleFactures.length) {
            setSelectedFactureIds(new Set());
        } else {
            setSelectedFactureIds(new Set(eligibleFactures.map(f => f.id)));
        }
    };

    // ─── TRANSITIONS DU STEPPER ──────────────────────────────────────────────

    const handleGoToStep2 = () => {
        if (checkedFactures.length === 0) return;
        if (!selectionNumber) {
            setSelectionNumber(generateNextSelectionNumber());
        }
        setActiveStep(1);
    };

    const handleGoToStep3 = () => {
        if (!simulationNumber) {
            setSimulationNumber(generateNextSimulationNumber());
        }
        setActiveStep(2);
    };

    const handleValidateTransfer = () => {
        if (checkedFactures.length === 0) return;

        const nextTrfNum = generateNextTransferNumber();
        const settings = getSageSettings();
        const fileContent = buildSageExportFileContent(simulationResult.ecritures, settings.exportSettings);
        const ymd = dayjs().format('YYYYMMDD');
        const filename = `${settings.exportSettings.prefixeFichier || 'TM_BASCULE_'}${ymd}.txt`;

        const record = {
            id: 'TRF_' + Date.now(),
            numeroTransfert: nextTrfNum,
            numeroSimulation: simulationNumber || generateNextSimulationNumber(),
            numeroSelection: selectionNumber || generateNextSelectionNumber(),
            journalPeriodStr: `FAC — ${dayjs(dateDebut).format('MMMM YYYY')} (du ${dayjs(dateDebut).format('DD/MM')} au ${dayjs(dateFin).format('DD/MM/YYYY')})`,
            dateValidation: new Date().toISOString(),
            user: currentUser,
            nomFichierSage: filename,
            repertoireDestination: settings.exportSettings.dossierDestination || 'C:\\Users\\HP\\Desktop',
            formatCodage: `Texte délimité (${settings.exportSettings.separateur || ';'}) — ${settings.exportSettings.encodage || 'UTF-8'}`,
            codeDossierSage: settings.exportSettings.codeDossierSage || 'AFRICA FOR TOURISM',
            factures: checkedFactures,
            nombreLignesEcriture: simulationResult.ecritures.length,
            totalDebit: simulationResult.totalDebit,
            totalCredit: simulationResult.totalCredit,
            fileContent,
            statut: 'TRANSFÉRÉ'
        };

        // Sauvegarde persistante & marquage factures
        saveTransferRecord(record);
        setTransferRecord(record);

        // Téléchargement immédiat du fichier SAGE
        downloadSageFile(fileContent, filename);

        // Recharger les factures pour refléter le statut transféré
        dispatch(fetchFactures());

        setActiveStep(3);
    };

    const handleResetWorkflow = () => {
        setActiveStep(0);
        setSelectedFactureIds(new Set());
        setSelectionNumber('');
        setSimulationNumber('');
        setTransferRecord(null);
        dispatch(fetchFactures());
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <PageHeader
                title="Transfert des écritures vers SAGE"
                subtitle="Procédure sécurisée de sélection, contrôle, simulation et validation de la bascule comptable"
                icon={TransferIcon}
                breadcrumbs={[
                    { label: 'Interface SAGE', path: '/sage/parametres' },
                    { label: 'Transfert des écritures', path: '/sage/transfert' }
                ]}
            />

            {/* STEPPER PROGRESSION */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white' }} className="no-print">
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STEPS.map((label, index) => (
                        <Step key={label}>
                            <StepLabel
                                StepIconProps={{
                                    sx: {
                                        '&.Mui-active': { color: '#0f172a' },
                                        '&.Mui-completed': { color: '#16a34a' }
                                    }
                                }}
                            >
                                <Typography variant="caption" fontWeight={activeStep === index ? 'bold' : 500}>
                                    {label}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            {/* ═════════════════════════════════════════════════════════════ */}
            {/* ÉTAPE 1 : SÉLECTION DES ÉCRITURES                             */}
            {/* ═════════════════════════════════════════════════════════════ */}
            {activeStep === 0 && (
                <Box>
                    {/* Filtres de période & actions */}
                    <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                    type="date"
                                    fullWidth
                                    size="small"
                                    label="Période : Date début"
                                    InputLabelProps={{ shrink: true }}
                                    value={dateDebut}
                                    onChange={(e) => setDateDebut(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <TextField
                                    type="date"
                                    fullWidth
                                    size="small"
                                    label="Période : Date fin"
                                    InputLabelProps={{ shrink: true }}
                                    value={dateFin}
                                    onChange={(e) => setDateFin(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4} md={6}>
                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => dispatch(fetchFactures())}
                                        sx={{ textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Actualiser
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        endIcon={<NextIcon />}
                                        disabled={checkedFactures.length === 0}
                                        onClick={handleGoToStep2}
                                        sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
                                    >
                                        Poursuivre vers le contrôle ({checkedFactures.length})
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Ligne des totaux dynamiques au-dessus du tableau */}
                    <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: '8px', bgcolor: '#0f172a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                            {checkedFactures.length} facture{checkedFactures.length > 1 ? 's' : ''} sélectionnée{checkedFactures.length > 1 ? 's' : ''} sur {eligibleFactures.length} éligible{eligibleFactures.length > 1 ? 's' : ''}
                        </Typography>
                        <Stack direction="row" spacing={3} alignItems="center">
                            <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>Total HT (Marge)</Typography>
                                <Typography variant="body2" fontWeight="bold">{formatCurrency(totalSelectedHT)} CFA</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>Total TVA</Typography>
                                <Typography variant="body2" fontWeight="bold">{formatCurrency(totalSelectedTVA)} CFA</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>Total TTC</Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#86efac' }}>{formatCurrency(totalSelectedTTC)} CFA</Typography>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Tableau des factures éligibles */}
                    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', bgcolor: 'white', overflow: 'hidden' }}>
                        <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Button size="small" onClick={handleSelectAll} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                {selectedFactureIds.size === eligibleFactures.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                Seules les factures non transférées de la période apparaissent ci-dessous
                            </Typography>
                        </Box>

                        <Box sx={{ overflowX: 'auto' }}>
                            <Box sx={{ minWidth: 900 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '50px 130px 100px 1.5fr 120px 120px 110px 120px 110px', bgcolor: '#0f172a', color: 'white', p: 1.2, fontWeight: 'bold', fontSize: '0.8rem' }}>
                                    <Box sx={{ textAlign: 'center' }}>✓</Box>
                                    <Box>N° Facture</Box>
                                    <Box>Date</Box>
                                    <Box>Client</Box>
                                    <Box>Compte Tiers</Box>
                                    <Box>N° Dossier / PNR</Box>
                                    <Box>Nature</Box>
                                    <Box sx={{ textAlign: 'right' }}>Montant TTC</Box>
                                    <Box sx={{ textAlign: 'center' }}>Statut</Box>
                                </Box>

                                {fetchStatus === 'loading' ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <CircularProgress size={30} />
                                    </Box>
                                ) : eligibleFactures.length === 0 ? (
                                    <Box sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
                                        Aucune facture éligible à transférer pour cette période.
                                    </Box>
                                ) : (
                                    eligibleFactures.map((f, idx) => {
                                        const isChecked = selectedFactureIds.has(f.id);
                                        return (
                                            <Box
                                                key={f.id || idx}
                                                onClick={() => handleToggleFacture(f.id)}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '50px 130px 100px 1.5fr 120px 120px 110px 120px 110px',
                                                    p: 1,
                                                    alignItems: 'center',
                                                    fontSize: '0.82rem',
                                                    borderBottom: '1px solid #e2e8f0',
                                                    bgcolor: isChecked ? 'rgba(15, 23, 42, 0.04)' : (idx % 2 === 0 ? 'white' : '#f8fafc'),
                                                    cursor: 'pointer',
                                                    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.08)' }
                                                }}
                                            >
                                                <Box sx={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={isChecked}
                                                        onChange={() => handleToggleFacture(f.id)}
                                                    />
                                                </Box>
                                                <Box sx={{ fontWeight: 700 }}>{f.numeroFacture}</Box>
                                                <Box sx={{ color: 'text.secondary' }}>{f.dateFacture}</Box>
                                                <Box sx={{ fontWeight: 600 }}>{f.clientNom}</Box>
                                                <Box sx={{ color: f.clientCodeTiers === '4111Àcréer' ? '#dc2626' : '#2563eb', fontWeight: 600 }}>
                                                    {f.clientCodeTiers}
                                                </Box>
                                                <Box sx={{ color: 'text.secondary' }}>{f.refDossier}</Box>
                                                <Box>{f.nature}</Box>
                                                <Box sx={{ textAlign: 'right', fontWeight: 800 }}>{formatCurrency(f.montantTTC)}</Box>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Chip size="small" label="À transférer" color="warning" />
                                                </Box>
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* ═════════════════════════════════════════════════════════════ */}
            {/* ÉTAPE 2 : CONTRÔLE DE LA SÉLECTION                            */}
            {/* ═════════════════════════════════════════════════════════════ */}
            {activeStep === 1 && (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }} className="no-print">
                        <Button
                            variant="outlined"
                            startIcon={<BackIcon />}
                            onClick={() => setActiveStep(0)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Modifier la sélection
                        </Button>
                        <Button
                            variant="contained"
                            endIcon={<NextIcon />}
                            onClick={handleGoToStep3}
                            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
                        >
                            Lancer la simulation (Pré-bascule)
                        </Button>
                    </Stack>

                    <ListeSelectionViewer
                        selectionNumber={selectionNumber}
                        periode={{
                            start: dayjs(dateDebut).format('DD/MM/YYYY'),
                            end: dayjs(dateFin).format('DD/MM/YYYY')
                        }}
                        factures={checkedFactures}
                        comptesRecap={simulationResult.comptesRecap}
                        user={currentUser}
                    />
                </Box>
            )}

            {/* ═════════════════════════════════════════════════════════════ */}
            {/* ÉTAPE 3 : SIMULATION (PRÉ-BASCULE)                            */}
            {/* ═════════════════════════════════════════════════════════════ */}
            {activeStep === 2 && (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }} className="no-print">
                        <Button
                            variant="outlined"
                            startIcon={<BackIcon />}
                            onClick={() => setActiveStep(1)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Retour au contrôle
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckIcon />}
                            disabled={!simulationResult.isEquilibre}
                            onClick={handleValidateTransfer}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                            Valider et générer le fichier SAGE
                        </Button>
                    </Stack>

                    {simulationResult.anomalies.length > 0 && (
                        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2.5, borderRadius: '8px' }} className="no-print">
                            <strong>Contrôles automatiques :</strong> {simulationResult.anomalies.length} point(s) d'attention détecté(s).
                            <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                                {simulationResult.anomalies.map((a, i) => (
                                    <li key={i}>{a.message}</li>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    <PreBasculeSimulationViewer
                        simulationNumber={simulationNumber}
                        selectionNumber={selectionNumber}
                        journalPeriodStr={`FAC — ${dayjs(dateDebut).format('MMMM YYYY')} (du ${dayjs(dateDebut).format('DD/MM')} au ${dayjs(dateFin).format('DD/MM/YYYY')})`}
                        nombreFactures={checkedFactures.length}
                        ecritures={simulationResult.ecritures}
                        controleStats={simulationResult.controleStats}
                        user={currentUser}
                    />
                </Box>
            )}

            {/* ═════════════════════════════════════════════════════════════ */}
            {/* ÉTAPE 4 : VALIDATION DU TRANSFERT                             */}
            {/* ═════════════════════════════════════════════════════════════ */}
            {activeStep === 3 && transferRecord && (
                <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }} className="no-print">
                        <Alert severity="success" sx={{ py: 0.5, px: 2, borderRadius: '8px' }}>
                            Transfert validé avec succès. Le fichier d'import SAGE a été téléchargé sur votre poste.
                        </Alert>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={handleResetWorkflow}
                            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}
                        >
                            Nouveau transfert
                        </Button>
                    </Stack>

                    <BordereauValidationViewer
                        transferRecord={transferRecord}
                    />
                </Box>
            )}
        </Box>
    );
};

export default TransfertEcrituresPage;
